/* RESPONSIBLE TEAM: team-inbox-management */
import type AppService from 'embercom/services/app-service';
import Service from '@ember/service';
import { inject as service } from '@ember/service';
import type Session from 'embercom/services/session';
import { UNASSIGNED_ID } from 'embercom/objects/inbox/admin-summary';

export const BPO_REPORTING_HELP_URL =
  'https://www.intercom.com/help/en/articles/12068195-control-teammate-and-team-visibility#h_8c8fcfa9a5';

export interface TeamAccessControlData {
  access_restrictions_enabled: boolean;
  admin_team_ids?: number[];
  admin_team_member_ids?: number[];
  bot_ids?: number[];
  operator_id?: number;
}

export default class BpoAccessControlService extends Service {
  @service declare session: Session;
  @service declare appService: AppService;

  get isAccessRestrictionsEnabled(): boolean {
    return (
      this.isLoaded &&
      this.session.teammate?.teamAccessControl?.access_restrictions_enabled === true
    );
  }

  get isLoaded(): boolean {
    // Service is "loaded" when session has teammate data
    return this.session.isTeammateLoaded;
  }

  private get teamAccessControlData(): TeamAccessControlData | null {
    return this.isLoaded ? this.session.teammate?.teamAccessControl || null : null;
  }

  get hasLimitedReportingAccess() {
    return !this.appService.app.currentAdmin?.currentAppPermissions
      ?.can_access_all_teammates_and_team_details;
  }

  /**
   * Check if an admin ID is accessible based on BPO separation rules
   * Returns true if the admin should be visible to the current user
   */
  adminAccessible(adminId: number | string): boolean {
    // If service is not loaded yet, Deny access to avoid leaking anything
    if (!this.isLoaded) {
      return false;
    }

    // If access restrictions are not enabled, allow all access
    let accessControlData = this.teamAccessControlData;
    if (!this.isAccessRestrictionsEnabled || !accessControlData) {
      return true;
    }

    let adminIdNum = typeof adminId === 'string' ? parseInt(adminId, 10) : adminId;

    // Allow access to invalid/null IDs (they'll be handled elsewhere)
    if (!adminIdNum || isNaN(adminIdNum)) {
      return true;
    }

    // Allow access to current user
    if (this.isLoaded && adminIdNum === this.session.teammate.id) {
      return true;
    }

    // Allow access to unassigned (ID 0)
    if (adminIdNum === UNASSIGNED_ID) {
      return true;
    }

    // Allow access to app operator
    if (accessControlData.operator_id && adminIdNum === accessControlData.operator_id) {
      return true;
    }

    // Allow access to team members
    if (accessControlData.admin_team_member_ids?.includes(adminIdNum)) {
      return true;
    }

    // Allow access to bots
    if (accessControlData.bot_ids?.includes(adminIdNum)) {
      return true;
    }

    // Deny access by default
    return false;
  }

  /**
   * Check if a team ID is accessible based on BPO separation rules
   * Returns true if the team should be visible to the current user
   */
  teamAccessible(teamId: number | string): boolean {
    // If service is not loaded yet, Deny access to avoid leaking anything
    if (!this.isLoaded) {
      return false;
    }

    // If access restrictions are not enabled, allow all access
    let accessControlData = this.teamAccessControlData;
    if (!this.isAccessRestrictionsEnabled || !accessControlData) {
      return true;
    }

    let teamIdNum = typeof teamId === 'string' ? parseInt(teamId, 10) : teamId;

    // Allow access to invalid/null IDs (they'll be handled elsewhere)
    if (!teamIdNum || isNaN(teamIdNum)) {
      return true;
    }

    // Allow access to unassigned (ID 0)
    if (teamIdNum === UNASSIGNED_ID) {
      return true;
    }

    // Allow access to admin's teams
    if (accessControlData.admin_team_ids?.includes(teamIdNum)) {
      return true;
    }

    // Deny access by default
    return false;
  }
}

declare module '@ember/service' {
  interface Registry {
    'bpo-access-control': BpoAccessControlService;
    bpoAccessControl: BpoAccessControlService;
  }
}
